import React, { useMemo, useState } from 'react';
import type { DateRange } from '../../types/dashboard';
import type { TerritoryRow } from '../../utils/territoryRows';
import { Panel } from '../ui/Panel';
import { SortableTh } from '../ui/SortableTh';
import { ExportButton, SearchInput, ToggleSwitch } from '../ui/TableControls';
import { StatusBadge } from '../ui/StatusBadge';
import { EmptyState } from '../ui/EmptyState';
import { useSort } from '../../hooks/useSort';
import { statusRules } from '../../utils/status';
import { monthLabel } from '../../utils/calendar';
import { formatChange, formatValue, isCountMetric, metricMeta, type TrendMetric } from '../../utils/metricConfig';
import { downloadCsv } from '../../utils/csv';
type Key = 'name' | 'coach' | 'value' | 'monthly' | 'bench' | 'gap' | 'mom' | 'yoy' | 'status';
const STATUS_ORDER = {
  'Priority review': 0,
  'Below network': 1,
  Ramping: 2,
  'On par': 3,
  'Above network': 4
};
function accessor(r: TerritoryRow, k: Key): number | string | null {
  switch (k) {
    case 'name':
      return r.territory.name;
    case 'coach':
      return r.territory.fbc;
    case 'value':
      return r.value;
    case 'monthly':
      return r.monthly;
    case 'bench':
      return r.benchMonthly;
    case 'gap':
      return r.gap;
    case 'mom':
      return r.mom;
    case 'yoy':
      return r.yoy;
    case 'status':
      return STATUS_ORDER[r.status];
  }
}
interface RankingTableProps {
  rows: TerritoryRow[];
  metric: TrendMetric;
  range: DateRange;
  onOpen: (territoryId: string) => void;
}
export function RankingTable({
  rows,
  metric,
  range,
  onOpen
}: RankingTableProps) {
  const [query, setQuery] = useState('');
  const [belowOnly, setBelowOnly] = useState(false);
  const meta = metricMeta(metric);
  const count = isCountMetric(metric);
  const fmt = (v: number) => formatValue(metric, v);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => (!belowOnly || r.gap < 0) && (!q || [r.territory.name, r.territory.state, r.territory.ad ?? '', r.territory.fbc].some(s => s.toLowerCase().includes(q))));
  }, [rows, query, belowOnly]);
  const {
    sorted,
    sort,
    onSort
  } = useSort<TerritoryRow, Key>(filtered, {
    key: 'gap',
    dir: 'asc'
  }, accessor);
  const maxAbs = Math.max(0.01, ...rows.map(r => Math.abs(r.gap)));
  const totalLabel = metric === 'members' ? 'New members (period)' : count ? 'Total (period)' : `${meta.label} (period)`;
  const benchLabel = count ? 'Network avg / terr. / mo' : 'Network (period)';
  const momLabel = `MoM · ${monthLabel(range.end)}`;
  const changeCell = (v: number | null) => v === null ? <span className="text-ink-subtle" data-id="element-348" data-mp="262padrwcw35y">—</span> : <span className={v < 0 ? 'text-negative' : 'text-positive'} data-id="element-349" data-mp="265tkf5wi5t4j">{formatChange(metric, v)}</span>;
  const exportCsv = () => downloadCsv(`i9-territory-${metric}.csv`, ['Territory', 'AD', 'FBC', totalLabel, ...(count ? ['Active months', 'Avg per month'] : []), benchLabel, 'Variance vs network', `MoM (${monthLabel(range.end)} vs ${monthLabel(range.end - 1)})`, 'YoY (period vs prior year)', 'Status'], sorted.map(r => [r.territory.name, r.territory.ad ?? '—', r.territory.fbc, Number(r.value.toFixed(4)), ...(count ? [r.activeMonths, Number(r.monthly.toFixed(4))] : []), Number(r.benchMonthly.toFixed(4)), formatChange(metric, r.gap), r.mom === null ? '' : formatChange(metric, r.mom), r.yoy === null ? '' : formatChange(metric, r.yoy), r.status]));
  return <Panel title="Territory performance" definitionId="performanceStatus" subtitle={count ? `${meta.label} · ${range.label} · each territory’s average per active month vs the network average per active territory per month · click a row to drill through` : `${meta.label} · ${range.label} · each territory vs the network rate for the same period · click a row to drill through`} actions={<>
          <SearchInput value={query} onChange={setQuery} placeholder="Search territory, AD or FBC" data-id="element-351" data-mp="2ze9oqvd4xsay" />
          <ToggleSwitch checked={belowOnly} onChange={setBelowOnly} label="Below network avg only" data-id="element-352" data-mp="2vco42nud09q7" />
          <ExportButton onClick={exportCsv} data-id="element-353" data-mp="2qlzbt6auv8q0" />
        </>} bodyClassName="p-0" data-id="element-350" data-mp="2frmf8rykwg4g">
      {sorted.length === 0 ? <EmptyState title="No territories match" description={belowOnly ? 'No territory in the current scope is below the network average.' : 'Try a different search term.'} action={<button type="button" onClick={() => {
      setQuery('');
      setBelowOnly(false);
    }} className="text-xs font-semibold text-navy hover:underline" data-id="element-355" data-mp="2bnx88w3tqn8r">
              Clear search and toggle
            </button>} data-id="element-354" data-mp="2lno4b2819aay" /> : <div className="max-h-[520px] overflow-auto" data-id="element-356" data-mp="22qd51hmeigvi">
          <table className="w-full min-w-[1120px] border-collapse text-[12.5px]" data-id="element-357" data-mp="5up5jcj4wk3o">
            <thead className="sticky top-0 z-10 bg-navy-50 text-navy" data-id="element-358" data-mp="wduwzl0m5ke">
              <tr data-id="element-359" data-mp="8g6a5b5wd274">
                <SortableTh label="Territory" sortKey="name" sort={sort} onSort={onSort} align="left" className="pl-5" data-id="element-360" data-mp="8goiv79qx8vx" />
                <SortableTh label="FBC / AD" sortKey="coach" sort={sort} onSort={onSort} align="left" data-id="element-361" data-mp="de7vy3upjfma" />
                <SortableTh label={totalLabel} sortKey="value" sort={sort} onSort={onSort} data-id="element-362" data-mp="i7ar2gf4ux4l" />
                {count && <SortableTh label="Avg / month" sortKey="monthly" sort={sort} onSort={onSort} data-id="element-363" data-mp="n3rfzcu5pfiq" />}
                <SortableTh label={benchLabel} sortKey="bench" sort={sort} onSort={onSort} data-id="element-364" data-mp="3lnzxm2epu6u9" />
                <SortableTh label="Variance vs network" sortKey="gap" sort={sort} onSort={onSort} data-id="element-365" data-mp="3qmuirb05o7hj" />
                <SortableTh label={momLabel} sortKey="mom" sort={sort} onSort={onSort} data-id="element-366" data-mp="3vjtbpq8328e5" />
                <SortableTh label="YoY · period" sortKey="yoy" sort={sort} onSort={onSort} data-id="element-367" data-mp="3hbsicjo1jol" />
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} align="left" className="pr-5" data-id="element-368" data-mp="1aveyb6dcri9e" />
              </tr>
            </thead>
            <tbody className="tabular-nums" data-id="element-369" data-mp="1ey38pihpsx8h">
              {sorted.map((r, i) => {
            const go = () => onOpen(r.territory.id);
            return <tr key={r.territory.id} tabIndex={0} onClick={go} onKeyDown={e => e.key === 'Enter' && go()} className={`cursor-pointer border-b border-line transition-colors duration-100 hover:bg-navy-50 focus-visible:bg-navy-50 focus-visible:outline-none ${i % 2 ? 'bg-canvas/50' : ''}`} data-id="element-370" data-mp="36x9xwek7dml8" data-mpi={i}>
                    <td className="whitespace-nowrap py-2.5 pl-5 pr-3" data-id="element-371" data-mp="2o94wveuxkfq4">
                      <span className="font-semibold text-navy" data-id="element-372" data-mp="31zgv7vi3zapp">{r.territory.name}</span>
                      <span className="ml-1.5 text-ink-subtle" data-id="element-373" data-mp="2x6xggv7oq9jb">{r.territory.state}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-ink-muted" data-id="element-374" data-mp="29h338cjgeiyj">
                      {r.territory.fbc}
                      <span className="text-ink-subtle" data-id="element-375" data-mp="24h8eofn5i5gn"> · AD {r.territory.ad ?? '—'}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold" data-id="element-376" data-mp="jzfrrjobitmv">{fmt(r.value)}</td>
                    {count && <td className="px-3 py-2.5 text-right" title={`${fmt(r.value)} ÷ ${r.activeMonths} active months`} data-id="element-377" data-mp="b140dffmnrtd">
                        {fmt(r.monthly)}
                        {r.activeMonths < range.end - range.start + 1 && <span className="ml-1 text-[11px] text-ink-subtle" data-id="element-378" data-mp="13ozs05qcatja">({r.activeMonths} mo)</span>}
                      </td>}
                    <td className="px-3 py-2.5 text-right text-ink-muted" data-id="element-379" data-mp="17oigfbhddmqv">{fmt(r.benchMonthly)}</td>
                    <td className="px-3 py-2.5" data-id="element-380" data-mp="ttg0rwmq0j0v">
                      <div className="flex items-center justify-end gap-2" data-id="element-381" data-mp="yp3r4fcxvi7y">
                        <div className="relative h-2 w-24 rounded-sm bg-canvas" aria-hidden data-id="element-382" data-mp="3egrmi6cb12on">
                          <span className="absolute inset-y-0 left-1/2 w-px bg-line-strong" data-id="element-383" data-mp="3jg4a4tb02dsa" />
                          <span className={`absolute inset-y-0 ${r.gap < 0 ? 'right-1/2 rounded-l-sm bg-negative/70' : 'left-1/2 rounded-r-sm bg-positive/70'}`} style={{
                      width: `${Math.abs(r.gap) / maxAbs * 50}%`
                    }} data-id="element-384" data-mp="eyb3ewkpehob" />
                        </div>
                        <span className={`w-16 text-right font-medium ${r.gap < 0 ? 'text-negative' : 'text-positive'}`} data-id="element-385" data-mp="2dcpttju5jh">{formatChange(metric, r.gap)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right" data-id="element-386" data-mp="srai4wcnw7ut">{changeCell(r.mom)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-387" data-mp="nyhbu41jorgm">{changeCell(r.yoy)}</td>
                    <td className="py-2.5 pl-3 pr-5" data-id="element-388" data-mp="j0plvfkgnff2"><StatusBadge status={r.status} data-id="element-389" data-mp="izehbdj6y3mw" /></td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 border-t border-line px-5 py-3 text-[11.5px] text-ink-muted" data-id="element-390" data-mp="1bobpgntouk1x">
        <span className="font-semibold text-ink" data-id="element-391" data-mp="1jyj3u4vdrlre">Status for {meta.label.toLowerCase()}</span>
        {statusRules(metric).map(s => <span key={s.status} className="flex items-center gap-1.5" data-id="element-392" data-mp="1o15n3hdzjycz">
            <StatusBadge status={s.status} data-id="element-393" data-mp="1sxea1gyfcvup" />
            {s.rule}
          </span>)}
        <span className="w-full text-ink-subtle" data-id="element-394" data-mp="1xyaa1jfnnxuu">
          MoM compares {monthLabel(range.end)} with {monthLabel(range.end - 1)} (latest completed month). YoY compares the full selected period with the same months one year earlier.
        </span>
      </div>
    </Panel>;
}